import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, FileText, Download, Check } from "lucide-react";
import { getTest, updateTest } from "../lib/api";
import LactateChart from "../components/LactateChart";
import TestDataTable from "../components/TestDataTable";
import ThresholdInputs from "../components/ThresholdInputs";
import ZonesTable from "../components/ZonesTable";
import ZoneGuideEditor from "../components/ZoneGuideEditor";
import { speedToPace, convertSpeed, convertSpeedRange } from "../lib/pace";
import { toast } from "sonner";

const AUTOSAVE_DELAY_MS = 1200;

const toNumOrNull = (v) => (v === "" || v == null ? null : Number(v));

function cleanPayload(test) {
  return {
    athlete_name: test.athlete_name,
    test_date: test.test_date,
    protocol: test.protocol,
    speed_unit: test.speed_unit,
    rows: (test.rows || []).map((r) => ({
      time: toNumOrNull(r.time),
      pace: r.pace || "",
      speed: toNumOrNull(r.speed),
      hr: toNumOrNull(r.hr),
      blood_lactate: toNumOrNull(r.blood_lactate),
      rpe: toNumOrNull(r.rpe),
    })),
    lt1: {
      hr: toNumOrNull(test.lt1?.hr),
      speed: toNumOrNull(test.lt1?.speed),
      pace: test.lt1?.pace || "",
      rpe: toNumOrNull(test.lt1?.rpe),
    },
    lt2: {
      hr: toNumOrNull(test.lt2?.hr),
      speed: toNumOrNull(test.lt2?.speed),
      pace: test.lt2?.pace || "",
      rpe: toNumOrNull(test.lt2?.rpe),
    },
    zones: test.zones || [],
    zone_guidance: test.zone_guidance || [],
    coach_note: test.coach_note || "",
  };
}

function recalcOnUnitChange(test, nextUnit) {
  const prevUnit = test.speed_unit;
  if (prevUnit === nextUnit) return test;
  const rows = (test.rows || []).map((r) => {
    const newSpeed =
      r.speed !== "" && r.speed != null ? convertSpeed(r.speed, prevUnit, nextUnit) : r.speed;
    return {
      ...r,
      speed: newSpeed,
      pace: newSpeed !== "" && newSpeed != null ? speedToPace(newSpeed, nextUnit) : r.pace || "",
    };
  });
  const convertThreshold = (th) => {
    if (!th) return th;
    const newSpeed =
      th.speed !== "" && th.speed != null ? convertSpeed(th.speed, prevUnit, nextUnit) : th.speed;
    return {
      ...th,
      speed: newSpeed,
      pace:
        newSpeed !== "" && newSpeed != null ? speedToPace(newSpeed, nextUnit) : th.pace || "",
    };
  };
  const zones = (test.zones || []).map((z) => ({
    ...z,
    speed_range: convertSpeedRange(z.speed_range || "", prevUnit, nextUnit),
  }));
  return {
    ...test,
    speed_unit: nextUnit,
    rows,
    lt1: convertThreshold(test.lt1),
    lt2: convertThreshold(test.lt2),
    zones,
  };
}

export default function TestEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLabel, setSavingLabel] = useState(""); // "", "saving", "saved"
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getTest(id);
        setTest(t);
      } catch {
        toast.error("Couldn't load test");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const saveNow = useCallback(
    async (currentTest, { silent = false } = {}) => {
      if (!currentTest || savingRef.current) return null;
      savingRef.current = true;
      setSavingLabel("saving");
      try {
        const updated = await updateTest(id, cleanPayload(currentTest));
        setTest(updated);
        dirtyRef.current = false;
        setSavingLabel("saved");
        setTimeout(() => setSavingLabel((v) => (v === "saved" ? "" : v)), 1800);
        if (!silent) toast.success("Saved");
        return updated;
      } catch (e) {
        setSavingLabel("");
        toast.error("Save failed");
        return null;
      } finally {
        savingRef.current = false;
      }
    },
    [id]
  );

  // Autosave on any dirty change, debounced
  useEffect(() => {
    if (!test || !dirtyRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveNow(test, { silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [test, saveNow]);

  const patch = (updater) => {
    dirtyRef.current = true;
    setTest((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  };

  const changeSpeedUnit = (unit) => {
    if (!test || test.speed_unit === unit) return;
    dirtyRef.current = true;
    setTest((prev) => recalcOnUnitChange(prev, unit));
    toast.success(`Converted to ${unit.toUpperCase()}`);
  };

  const goToReport = async () => {
    if (dirtyRef.current && test) {
      await saveNow(test, { silent: true });
    }
    navigate(`/tests/${id}/report`);
  };

  const exportPNG = async () => {
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const node = chartRef.current;
      if (!node) return;
      const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElement("a");
      link.download = `lactate-curve-${test.athlete_name.replace(/\s+/g, "_")}-${test.test_date}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("PNG exported");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading || !test) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  const labelCls =
    "block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1";
  const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-sm focus:outline-none font-mono-tir";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="test-editor-page">
      <Link
        to={`/athletes/${test.athlete_id}`}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-6"
        data-testid="back-to-athlete"
      >
        <ArrowLeft size={14} /> Back to {test.athlete_name}
      </Link>

      {/* Header */}
      <header className="border-b border-[var(--tir-ink)] pb-6 mb-8 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
            Lactate Test · Editor
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[var(--tir-ink)] leading-none">
            {test.athlete_name}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] min-w-[86px]"
            data-testid="autosave-indicator"
          >
            {savingLabel === "saving" && (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--tir-burgundy)] animate-pulse" />
                Saving…
              </>
            )}
            {savingLabel === "saved" && (
              <>
                <Check size={12} className="text-[var(--tir-burgundy)]" /> Saved
              </>
            )}
            {!savingLabel && <span>Autosaves on edit</span>}
          </span>
          <button
            onClick={exportPNG}
            disabled={exporting}
            data-testid="export-png-btn"
            className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
          >
            <Download size={14} /> PNG
          </button>
          <button
            onClick={goToReport}
            data-testid="view-report-btn"
            className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
          >
            <FileText size={14} /> View Report
          </button>
          <button
            onClick={() => saveNow(test)}
            disabled={savingLabel === "saving"}
            data-testid="save-test-btn"
            className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </header>

      {/* Meta row */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div>
          <label className={labelCls}>Test date</label>
          <input
            type="date"
            value={test.test_date || ""}
            onChange={(e) => patch({ test_date: e.target.value })}
            className={inputCls}
            data-testid="test-date-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Protocol</label>
          <input
            type="text"
            value={test.protocol || ""}
            onChange={(e) => patch({ protocol: e.target.value })}
            className={inputCls}
            data-testid="protocol-input"
          />
        </div>
        <div>
          <label className={labelCls}>Speed unit</label>
          <div
            className="inline-flex border border-[var(--tir-ink)] h-9 w-full"
            data-testid="speed-unit-toggle"
          >
            {["mph", "kph"].map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => changeSpeedUnit(u)}
                data-testid={`unit-${u}`}
                className={`flex-1 text-xs tracking-[0.22em] uppercase font-semibold transition-colors ${
                  test.speed_unit === u
                    ? "bg-[var(--tir-ink)] text-white"
                    : "bg-white text-[var(--tir-ink)] hover:bg-[var(--tir-ivory)]"
                }`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Two column grid: left inputs, right chart sticky */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <TestDataTable
            rows={test.rows || []}
            speedUnit={test.speed_unit}
            onChange={(rows) => patch({ rows })}
          />

          <ThresholdInputs
            lt1={test.lt1 || {}}
            lt2={test.lt2 || {}}
            speedUnit={test.speed_unit}
            onChangeLT1={(v) => patch({ lt1: v })}
            onChangeLT2={(v) => patch({ lt2: v })}
          />

          <ZonesTable
            zones={test.zones || []}
            speedUnit={test.speed_unit}
            onChange={(zones) => patch({ zones })}
          />

          <div>
            <label className={labelCls}>Coach's note</label>
            <textarea
              value={test.coach_note || ""}
              onChange={(e) => patch({ coach_note: e.target.value })}
              rows={5}
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] p-3 text-sm focus:outline-none font-sans leading-relaxed"
              data-testid="coach-note-input"
              placeholder="Write a personalised training recommendation for the athlete…"
            />
          </div>

          <ZoneGuideEditor
            value={test.zone_guidance}
            onChange={(zg) => patch({ zone_guidance: zg })}
          />
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="border border-[var(--tir-ink)] bg-white p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Your test curve
              </div>
              <div className="font-serif-display text-2xl text-[var(--tir-ink)] mb-4">
                Blood Lactate &amp; Heart Rate vs Speed
              </div>
              <div ref={chartRef} className="bg-white">
                <LactateChart test={test} height={420} />
              </div>
            </div>
            <p className="text-xs text-[var(--tir-muted)] mt-3 leading-relaxed">
              The chart updates live as you edit. Colored bands come from the zone speed ranges
              below. LT1/LT2 lines come from the threshold inputs.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
